import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { learningService } from "../../../api/learningService";
import Badge from "../../../components/ui/Badge";
import Field from "../../../components/ui/Field";
import Modal from "../../../components/ui/Modal";
import { useToast } from "../../../components/ui/useToast";
import { getErrorMessage } from "../../../utils/apiError";
import { getDeviceLocation, LocationError } from "../../../utils/geolocation";
import { formatManilaDateTime } from "../../../utils/manilaTime";
import { cx } from "../../../utils/classNames";
import {
  formatLearningHours,
  formatSeminarWhen,
  getSeminarState,
  LEARNING_KEY,
  myCheckInText,
  PARTICIPANT_SOURCE_LABELS,
  PARTICIPANT_STATUS,
  SEMINAR_MODE,
  selfCheckInView,
  SEMINAR_TYPE_LABELS,
} from "../learningUtils";
import { SkillChips } from "./SeminarCard";
import TrainingAgreement from "./TrainingAgreement";
import ui from "../../../components/ui/ui.module.css";
import styles from "../Learning.module.css";

// "I'm here": asks for the location only when the seminar needs it (on-site).
const SelfCheckIn = ({ seminar }) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const view = selfCheckInView(seminar);

  const mutation = useMutation({
    mutationFn: async () => {
      const location = seminar.selfCheckIn?.needsLocation
        ? await getDeviceLocation("check in")
        : undefined;
      return learningService.selfCheckIn(seminar.id, location);
    },
    onSuccess: (res) => {
      if (res?.locationVerified === false)
        toast.info(
          "Attended. It was recorded and flagged for HR to review, because your location couldn't be confirmed at the venue.",
        );
      else
        toast.success(
          res?.alreadyCheckedIn ? "Already checked in." : "Attended",
        );
      queryClient.invalidateQueries({ queryKey: [LEARNING_KEY] });
    },
  });
  const error =
    mutation.error instanceof LocationError
      ? mutation.error.message
      : mutation.error &&
        getErrorMessage(mutation.error, "Unable to check in.");

  if (view.kind === "none") return null;
  return (
    <div className="mb-3">
      {view.kind === "button" && (
        <button
          type="button"
          className={cx(ui.button, ui.primary)}
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
        >
          <i className="bi bi-check2-circle"></i>
          {mutation.isPending ? "Checking in…" : "I'm here"}
        </button>
      )}
      {view.kind !== "button" && (
        <div className={cx(ui.alert, ui.alertGray)}>
          <span>
            {view.kind === "qr" && <i className="bi bi-qr-code me-2"></i>}
            {view.text}
            {view.when && <div className={ui.hint}>{view.when}</div>}
          </span>
        </div>
      )}
      {error && (
        <div className={cx(ui.alert, ui.alertDanger, "mt-2")}>{error}</div>
      )}
    </div>
  );
};

// Seminar details with register / withdraw. `seminar` (from the list) is
// shown right away; the detail call refreshes seats and my status.
const SeminarDetailDrawer = ({ seminar: initial, onClose }) => {
  const toast = useToast();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [LEARNING_KEY, "seminar", initial.id],
    queryFn: () => learningService.getMySeminar(initial.id),
    placeholderData: initial,
  });
  const seminar = query.data ?? initial;
  const [accepted, setAccepted] = useState(false);

  const onDone = (message) => () => {
    toast.success(message);
    queryClient.invalidateQueries({ queryKey: [LEARNING_KEY] });
  };
  const onError = (fallback) => (error) =>
    toast.error(getErrorMessage(error, fallback));

  const enrollMutation = useMutation({
    mutationFn: () =>
      learningService.enroll(seminar.id, {
        acceptAgreement: !!seminar.agreement,
      }),
    onSuccess: onDone("You're registered."),
    onError: onError("Unable to register."),
  });

  const acceptMutation = useMutation({
    mutationFn: () => learningService.acceptAgreement(seminar.id),
    onSuccess: onDone("Agreement accepted."),
    onError: onError("Unable to accept the agreement."),
  });

  const withdrawMutation = useMutation({
    mutationFn: () => learningService.withdraw(seminar.id),
    onSuccess: () => {
      onDone("Registration withdrawn.")();
      onClose();
    },
    onError: onError("Unable to withdraw."),
  });

  const state = getSeminarState(seminar);
  const mode = SEMINAR_MODE[seminar.mode];
  const my = seminar.my;
  const myStatus = my && PARTICIPANT_STATUS[my.status];
  const isBusy =
    enrollMutation.isPending ||
    withdrawMutation.isPending ||
    acceptMutation.isPending;
  const agreement = seminar.status !== "CANCELLED" ? seminar.agreement : null;
  const needsAccepting =
    !!agreement?.mustAccept && (!my || my.status === "PENDING");
  const canAccept = needsAccepting && !!my && seminar.status === "SCHEDULED";

  const footer =
    seminar.canEnroll || my?.canWithdraw || canAccept ? (
      <>
        <button
          type="button"
          className={cx(ui.button, ui.secondary)}
          onClick={onClose}
        >
          Close
        </button>
        {seminar.canEnroll ? (
          <button
            type="button"
            className={cx(ui.button, ui.primary)}
            onClick={() => enrollMutation.mutate()}
            disabled={
              isBusy || seminar.seatsLeft === 0 || (!!agreement && !accepted)
            }
            title={
              agreement && !accepted
                ? "Read and accept the agreement first"
                : undefined
            }
          >
            {enrollMutation.isPending
              ? "Registering…"
              : seminar.seatsLeft === 0
                ? "Full"
                : "Register"}
          </button>
        ) : canAccept ? (
          <button
            type="button"
            className={cx(ui.button, ui.primary)}
            onClick={() => acceptMutation.mutate()}
            disabled={isBusy || !accepted}
            title={accepted ? undefined : "Tick the box to accept"}
          >
            {acceptMutation.isPending ? "Accepting…" : "Accept agreement"}
          </button>
        ) : (
          <button
            type="button"
            className={cx(ui.button, ui.dangerOutline)}
            onClick={() => withdrawMutation.mutate()}
            disabled={isBusy}
          >
            {withdrawMutation.isPending
              ? "Withdrawing…"
              : "Withdraw registration"}
          </button>
        )}
      </>
    ) : null;

  return (
    <Modal
      title={seminar.title}
      variant="drawer"
      onClose={onClose}
      footer={footer}
    >
      <div className="d-flex flex-wrap gap-1 mb-3">
        <Badge tone={state.tone}>{state.label}</Badge>
        {myStatus && <Badge tone={myStatus.tone}>{myStatus.label}</Badge>}
      </div>

      <SelfCheckIn seminar={seminar} />
      {agreement && (seminar.canEnroll || my) && (
        <TrainingAgreement
          agreement={agreement}
          accepted={accepted}
          onAcceptedChange={setAccepted}
        />
      )}
      {myCheckInText(my) && (
        <p className={cx(ui.hint, "mb-3")}>{myCheckInText(my)}</p>
      )}

      {seminar.status === "CANCELLED" && (
        <div className={cx(ui.alert, ui.alertDanger, "mb-3")}>
          Cancelled{seminar.cancelReason ? `: ${seminar.cancelReason}` : "."}
        </div>
      )}

      <div className={styles.detailGrid}>
        <Field label="When">
          {formatSeminarWhen(seminar.startAt, seminar.endAt)}
        </Field>
        <Field label="Type">
          {SEMINAR_TYPE_LABELS[seminar.type] ?? seminar.type} ·{" "}
          {mode?.label ?? seminar.mode}
        </Field>
        <Field label="Hours">
          {seminar.hours != null ? formatLearningHours(seminar.hours) : null}
        </Field>
        <Field label="Facilitator">{seminar.facilitator}</Field>
        <Field label="Venue">{seminar.venue}</Field>
        <Field label="Branch">{seminar.branchName ?? "All branches"}</Field>
        {seminar.seatsLeft != null && (
          <Field label="Seats left">{String(seminar.seatsLeft)}</Field>
        )}
        {my && (
          <Field label="How you joined">
            {PARTICIPANT_SOURCE_LABELS[my.source] ?? my.source}
          </Field>
        )}
      </div>

      {seminar.meetingLink && seminar.status !== "CANCELLED" && (
        <a
          href={seminar.meetingLink}
          target="_blank"
          rel="noopener noreferrer"
          className={cx(ui.button, ui.secondary, "mb-3")}
        >
          <i className="bi bi-camera-video"></i>
          Join meeting
        </a>
      )}

      {seminar.description && (
        <>
          <div className={ui.fieldLabel}>About</div>
          <p className={styles.description}>{seminar.description}</p>
        </>
      )}

      {seminar.skills?.length > 0 && (
        <>
          <div className={cx(ui.fieldLabel, "mb-1")}>
            Skills you earn by attending
          </div>
          <SkillChips skills={seminar.skills} />
        </>
      )}

      {my?.remarks && (
        <div className={cx(ui.alert, ui.alertGray, "mt-3")}>
          {my.checkInMethod ? "Remarks" : "HR remarks"}: {my.remarks}
          {my.markedAt && ` (${formatManilaDateTime(my.markedAt)})`}
        </div>
      )}

      {my && my.source === "ASSIGNED" && my.status === "PENDING" && (
        <p className={cx(ui.hint, "mt-3 mb-0")}>
          HR assigned you to this seminar. Ask HR if you can't attend.
        </p>
      )}
    </Modal>
  );
};

export default SeminarDetailDrawer;
